import React from "react"
import { flags, useFeatureFlags } from "../../../lib/feature-flags"
import ApplicationDenialMessage from "../../PtmApplicationStatus/ApplicationDenialMessage"
import MemberApplicationElectionDate from "../MemberApplicationElectionDate"
import { REQUIREMENT_DATA } from "../MembershipJourney/requirementData"
import { resolveUrlForArticles } from "../MembershipJourney/utils/urlUtils"
import {
  getStatusBackgroundColor,
  getStatusColor,
} from "../MembershipJourney/utils/displayUtils"
import {
  ActionButton,
  ActionButtonText,
  RequirementActions,
  RequirementCardStyled,
  RequirementDenialMessage,
  RequirementDescription,
  RequirementHeader,
  RequirementTitle,
  StatusBadgeSmall,
  StatusBadgeText,
  TextButton,
} from "../MembershipJourney/Styles/ProgressTracker.styles"
import {
  isDeniedPtmApplication,
  VIEW_APPLICATION_LABEL,
} from "../../../utils/prospect/ptmApplicationStatus"
import {
  SECTION_EMPLOYMENT_VERIFICATION_LABEL,
  SECTION_EMPLOYMENT_VERIFICATION_MESSAGE,
  showsSectionEmploymentVerification,
} from "../../../utils/sectionEmploymentVerification"

// Same catalog the Associate journey and the reciprocity card use for a
// Membership application, so Course Curriculum does not invent a second look.
const ENROLLMENT = REQUIREMENT_DATA.level3.requirements.enrollment

const IN_REVIEW_STATUSES = new Set([
  "submitted",
  "under_review",
  "changes_requested",
])

export const memberApplicationStatusPresentation = (
  status,
  { awaitingSectionEmploymentVerification = false } = {},
) => {
  const key = String(status || "").toLowerCase()
  if (
    showsSectionEmploymentVerification(
      key,
      awaitingSectionEmploymentVerification,
    )
  ) {
    return {
      statusKey: "submitted",
      label: SECTION_EMPLOYMENT_VERIFICATION_LABEL,
      message: SECTION_EMPLOYMENT_VERIFICATION_MESSAGE,
    }
  }
  if (IN_REVIEW_STATUSES.has(key)) {
    const copy = ENROLLMENT.statuses.submitted
    return {
      statusKey: "submitted",
      label: copy.label,
      message: copy.message,
    }
  }
  if (key === "approved") {
    const copy = ENROLLMENT.statuses.approved
    return {
      statusKey: "approved",
      label: copy.label,
      message: copy.message,
      secondaryLinkText: copy.secondaryLinkText,
      secondaryLinkUrl: copy.secondaryLinkUrl,
    }
  }
  if (isDeniedPtmApplication(key)) {
    const copy = ENROLLMENT.statuses.denied
    return {
      statusKey: "denied",
      label: copy.label,
      message: copy.message,
      secondaryLinkText: copy.secondaryLinkText,
      secondaryLinkUrl: copy.secondaryLinkUrl,
    }
  }
  return null
}

const MemberApplicationStatusCard = ({
  status,
  applicationId,
  applicationType,
  awaitingSectionEmploymentVerification = false,
  onViewApplication,
}) => {
  const [denialMessageEnabled] =
    useFeatureFlags([flags.FLAG_FEAT_APPLICATIONS_DENIAL_MESSAGE]) ?? []
  const presentation = memberApplicationStatusPresentation(status, {
    awaitingSectionEmploymentVerification,
  })
  if (!presentation) return null
  const isDenied = presentation.statusKey === "denied"

  return (
    <RequirementCardStyled data-testid="member-application-status">
      <RequirementHeader>
        <RequirementTitle>{ENROLLMENT.name}</RequirementTitle>
        <StatusBadgeSmall
          backgroundColor={getStatusBackgroundColor(presentation.statusKey)}
        >
          <StatusBadgeText textColor={getStatusColor(presentation.statusKey)}>
            {presentation.label}
          </StatusBadgeText>
        </StatusBadgeSmall>
      </RequirementHeader>
      <RequirementDescription>{presentation.message}</RequirementDescription>
      {presentation.statusKey === "approved" ? (
        <MemberApplicationElectionDate />
      ) : null}
      {isDenied && denialMessageEnabled && applicationId ? (
        <RequirementDenialMessage>
          <ApplicationDenialMessage
            applicationId={applicationId}
            applicationType={applicationType}
          />
        </RequirementDenialMessage>
      ) : null}
      <RequirementActions>
        {isDenied ? null : (
          <ActionButton type="button" onClick={onViewApplication}>
            <ActionButtonText>{VIEW_APPLICATION_LABEL}</ActionButtonText>
          </ActionButton>
        )}
        {presentation.secondaryLinkText && presentation.secondaryLinkUrl ? (
          <TextButton
            as="a"
            href={resolveUrlForArticles(presentation.secondaryLinkUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {presentation.secondaryLinkText}
          </TextButton>
        ) : null}
      </RequirementActions>
    </RequirementCardStyled>
  )
}

export default MemberApplicationStatusCard
